import * as THREE from 'three';
import { Random } from '../core/Random';
import { buildBull } from './fairgrounds';
import { neonBeerSign } from './builders/furniture';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, cyl, M, mat, mesh, registerNightGlow, sphere, texMat, worldBox } from './materials';
import { BAR_SLOT, ROOM_SHAPE, roomBounds, roomCenter } from './rooms';
import { dartboardTexture, signTexture, woodTexture } from './textures';

const WALL = 0.15;
const STUB = 0.3;

/** Where things are inside the Rusty Spur (local coordinates; the door is on +z). */
export const BAR_SPOTS = {
  bartender: { x: -2.5, z: -4.9 },
  barStool: { x: -2.5, z: -2.9 },
  darts: { x: -5.8, z: 0.2 },
  dartboard: { x: -8.95, z: 0.2 },
  bull: { x: 5.2, z: -2.2 },
  bullStep: { x: 5.2, z: 0.3 },
  poker: { x: 7.6, z: 3.2 },
  armTable: { x: 1.4, z: 2.6 },
  armSeat: { x: 1.4, z: 3.6 },
  regular: { x: 1.4, z: 1.6 },
  jukebox: { x: -7.9, z: 4.4 },
  pool: { x: -3.6, z: 2.6 },
  dice: { x: -0.4, z: -1.0 },
} as const satisfies Record<string, Vec2>;

interface Wall {
  upper: THREE.Group;
  nx: number;
  nz: number;
  px: number;
  pz: number;
}

function barStool(): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.2, 0.2, 0.08, mat('#7a1f17', { rough: 0.5 }), 0, 0.78, 0, 12));
  g.add(cyl(0.035, 0.035, 0.74, M.chrome, 0, 0.37, 0, 6));
  g.add(cyl(0.18, 0.2, 0.045, M.chrome, 0, 0.0225, 0, 10));
  return g;
}

function poolTable(): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#6b3f22' });
  g.add(box(2.5, 0.16, 1.4, wood, 0, 0.78, 0));
  g.add(box(2.24, 0.02, 1.14, mat('#1f6b3a', { rough: 1 }), 0, 0.87, 0, { cast: false }));
  for (const s of [-1, 1]) {
    g.add(box(2.5, 0.08, 0.13, wood, 0, 0.9, s * 0.635));
    g.add(box(0.13, 0.08, 1.4, wood, s * 1.185, 0.9, 0));
  }
  for (const x of [-1.05, 1.05]) for (const z of [-0.55, 0.55]) g.add(box(0.16, 0.7, 0.16, wood, x, 0.35, z));
  const colors = ['#ffd400', '#1d4e9e', '#c62828', '#6b2346', '#ff7a1a', '#2f7d32', '#111111', '#ffffff'];
  colors.forEach((c, i) =>
    g.add(
      mesh(
        new THREE.SphereGeometry(0.04, 8, 6),
        mat(c, { rough: 0.2 }),
        -0.4 + (i % 4) * 0.14,
        0.92,
        -0.1 + Math.floor(i / 4) * 0.14,
      ),
    ),
  );
  const cue = cyl(0.012, 0.018, 1.4, mat('#d9b45a'), 0.4, 0.93, 0.3, 6);
  cue.rotation.z = Math.PI / 2;
  cue.rotation.y = 0.4;
  g.add(cue);
  return g;
}

/** Radius of the dartboard face; the scoring rings fill DARTBOARD_SCORING of it. */
export const DARTBOARD_RADIUS = 0.26;

function dartboard(): { group: THREE.Group; chalk: THREE.Mesh } {
  const g = new THREE.Group();
  g.add(cyl(0.34, 0.34, 0.05, mat('#7a5a3a'), 0, 0, 0, 20).rotateX(Math.PI / 2));
  g.add(
    mesh(new THREE.CircleGeometry(DARTBOARD_RADIUS, 40), texMat(dartboardTexture()), 0, 0, 0.03, {
      cast: false,
    }),
  );
  // The chalkboard for scores (a darts game writes on it).
  g.add(box(0.66, 0.56, 0.03, mat('#5a3a1e'), 0.78, 0, -0.005));
  const chalk = mesh(new THREE.PlaneGeometry(0.6, 0.5), mat('#1e2b22', { rough: 1 }), 0.78, 0, 0.012, {
    cast: false,
  });
  g.add(chalk);
  return { group: g, chalk };
}

function jukebox(): { group: THREE.Group; glow: THREE.MeshStandardMaterial } {
  const g = new THREE.Group();
  g.add(box(0.9, 1.25, 0.6, mat('#7a2b22', { rough: 0.4, metal: 0.2 }), 0, 0.62, 0));
  const glow = new THREE.MeshStandardMaterial({
    color: '#ffb347',
    emissive: new THREE.Color('#ff8a1f'),
    emissiveIntensity: 1,
  });
  const arch = mesh(
    new THREE.CylinderGeometry(0.44, 0.44, 0.56, 16, 1, false, -Math.PI / 2, Math.PI),
    glow,
    0,
    1.25,
    0,
  );
  arch.rotation.z = Math.PI / 2;
  arch.rotation.y = Math.PI / 2;
  g.add(arch);
  g.add(
    box(0.6, 0.3, 0.02, mat('#f2efe6', { emissive: '#d0c8a0', emissiveIntensity: 0.4 }), 0, 0.9, 0.31, {
      cast: false,
    }),
  );
  for (let i = 0; i < 5; i++) g.add(box(0.06, 0.55, 0.02, glow, -0.3 + i * 0.15, 0.4, 0.31, { cast: false }));
  return { group: g, glow };
}

function pokerMachine(): {
  group: THREE.Group;
  screen: THREE.MeshStandardMaterial;
  screenMesh: THREE.Mesh;
} {
  const g = new THREE.Group();
  g.add(box(0.7, 1.5, 0.6, mat('#1b2a4a', { metal: 0.4, rough: 0.4 }), 0, 0.75, 0));
  const screen = new THREE.MeshStandardMaterial({
    color: '#0b2a5c',
    emissive: new THREE.Color('#2a6ad0'),
    emissiveIntensity: 0.9,
  });
  // Same shape as the video poker game's canvas (3:2), so it can play on the screen.
  const screenMesh = mesh(new THREE.PlaneGeometry(0.6, 0.4), screen, 0, 1.15, 0.305, { cast: false });
  g.add(screenMesh);
  g.add(box(0.6, 0.06, 0.25, mat('#c62828'), 0, 0.82, 0.38));
  const top = signTexture({
    w: 256,
    h: 64,
    bg: '#ffd400',
    fg: '#c62828',
    lines: [{ text: 'POKER', size: 50, font: 'Bangers' }],
  });
  g.add(mesh(new THREE.PlaneGeometry(0.6, 0.15), texMat(top), 0, 1.58, 0.305, { cast: false }));
  return { group: g, screen, screenMesh };
}

function barCounter(length: number): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#5a3820' });
  g.add(box(length, 1.05, 0.7, wood, 0, 0.525, 0));
  g.add(box(length + 0.1, 0.07, 0.86, mat('#3a2414', { rough: 0.3 }), 0, 1.08, 0.04));
  g.add(cyl(0.03, 0.03, length, M.chrome, 0, 0.22, 0.42, 6).rotateZ(Math.PI / 2));
  // Taps.
  for (let i = 0; i < 4; i++) {
    g.add(cyl(0.025, 0.025, 0.28, M.chrome, -0.6 + i * 0.22, 1.25, -0.15, 6));
    g.add(
      box(
        0.04,
        0.12,
        0.04,
        mat(['#c62828', '#1d4e9e', '#ffd400', '#2f7d32'][i] as string),
        -0.6 + i * 0.22,
        1.42,
        -0.15,
      ),
    );
  }
  return g;
}

function backBar(length: number, rng: Random): THREE.Group {
  const g = new THREE.Group();
  const wood = texMat(woodTexture(), { color: '#4a2e1a' });
  g.add(box(length, 0.9, 0.45, wood, 0, 0.45, 0));
  for (const y of [1.45, 1.95]) g.add(box(length, 0.05, 0.3, wood, 0, y, -0.05));
  g.add(box(length, 1.4, 0.03, mat('#2a3a44', { rough: 0.1, metal: 0.6 }), 0, 1.75, -0.2, { cast: false }));
  for (const y of [1.48, 1.98])
    for (let x = -length / 2 + 0.2; x < length / 2 - 0.1; x += 0.22) {
      const c = rng.pick(['#5a3a10', '#2f5d2f', '#8a2b22', '#d9c9a0', '#3a3a3a']);
      g.add(cyl(0.045, 0.05, 0.3, mat(c, { rough: 0.15, metal: 0.1 }), x, y + 0.17, -0.05, 8));
    }
  return g;
}

function barTable(): THREE.Group {
  const g = new THREE.Group();
  g.add(cyl(0.45, 0.45, 0.05, texMat(woodTexture(), { color: '#6b4a2b' }), 0, 0.75, 0, 14));
  g.add(cyl(0.05, 0.05, 0.72, M.black, 0, 0.36, 0, 6));
  g.add(cyl(0.25, 0.28, 0.03, M.black, 0, 0.015, 0, 10));
  return g;
}

function mechBullPen(): { group: THREE.Group; bull: THREE.Group } {
  const g = new THREE.Group();
  const pad = mat('#c62828', { rough: 0.6 });
  const ring = new THREE.Mesh(new THREE.TorusGeometry(2.0, 0.32, 8, 24), pad);
  ring.rotation.x = Math.PI / 2;
  ring.position.y = 0.32;
  ring.castShadow = true;
  g.add(ring);
  g.add(
    mesh(new THREE.CircleGeometry(1.9, 24), mat('#ffd400', { rough: 0.8 }), 0, 0.02, 0, {
      cast: false,
    }).rotateX(-Math.PI / 2),
  );
  g.add(cyl(0.35, 0.45, 0.6, M.steel, 0, 0.3, 0, 12));
  const bull = new THREE.Group();
  const model = buildBull('#6a6f74');
  model.group.position.y = -0.2;
  bull.add(model.group);
  bull.position.y = 0.5;
  g.add(bull);
  return { group: g, bull };
}

/**
 * Inside the Rusty Spur: a long bar, a pool table, darts, video poker, a jukebox, an
 * arm-wrestling table and a mechanical bull in an inflatable ring. Same cutaway walls
 * as the trailers.
 */
export class BarRoom {
  readonly slot = BAR_SLOT;
  readonly kind = 'bar' as const;
  readonly group = new THREE.Group();
  readonly center: Vec2 = roomCenter(BAR_SLOT);
  private readonly walls: Wall[] = [];
  private readonly ceiling = new THREE.Group();
  readonly mechBull: THREE.Group;
  /** The dartboard (faces +x) and its chalkboard, for the darts game. */
  readonly dartboard!: THREE.Group;
  readonly chalk!: THREE.Mesh;
  /** The first poker machine's screen. */
  readonly pokerScreenMesh!: THREE.Mesh;
  /** Things to outline when you're about to use them. */
  readonly props: Record<'poker' | 'jukebox' | 'pool' | 'arm', THREE.Object3D> = {
    poker: new THREE.Group(),
    jukebox: new THREE.Group(),
    pool: new THREE.Group(),
    arm: new THREE.Group(),
  };
  /** Somebody's riding the mechanical bull and the ride drives it. */
  bullDriven = false;
  private readonly jukeGlow: THREE.MeshStandardMaterial;
  private readonly pokerScreen: THREE.MeshStandardMaterial;
  private readonly rng = new Random(77);
  /** Set by the bar while someone's riding it. */
  bucking = 0;

  constructor(parent: THREE.Object3D, collision: CollisionWorld) {
    const { length: L, depth: D, height: H, doorX } = ROOM_SHAPE.bar;
    const c = this.center;
    this.group.position.set(c.x, 0, c.z);
    const wallMat = texMat(woodTexture(), { color: '#d9a878' });
    // Floor: old planks, sticky.
    this.group.add(
      mesh(
        worldBox(L + 2 * WALL, 0.1, D + 2 * WALL, 1.2),
        texMat(woodTexture(), { color: '#b07c52', rough: 0.6 }),
        0,
        -0.05,
        0,
        { cast: false },
      ),
    );
    const addWall = (len: number, cx: number, cz: number, alongX: boolean, nx: number, nz: number): void => {
      const w = alongX ? len : WALL;
      const d = alongX ? WALL : len;
      this.group.add(mesh(worldBox(w, STUB, d, 1.2), wallMat, cx, STUB / 2, cz, { cast: false }));
      const upper = new THREE.Group();
      upper.add(mesh(worldBox(w, H - STUB, d, 1.2), wallMat, cx, STUB + (H - STUB) / 2, cz));
      this.group.add(upper);
      this.walls.push({ upper, nx, nz, px: c.x + cx - nx * (WALL / 2), pz: c.z + cz - nz * (WALL / 2) });
      collision.addRect(c.x + cx, c.z + cz, w, d);
    };
    addWall(L + 2 * WALL, 0, -D / 2 - WALL / 2, true, 0, -1);
    addWall(L + 2 * WALL, 0, D / 2 + WALL / 2, true, 0, 1);
    addWall(D, -L / 2 - WALL / 2, 0, false, -1, 0);
    addWall(D, L / 2 + WALL / 2, 0, false, 1, 0);
    const [back, front, west, east] = this.walls;

    // A plank ceiling with lamps over the pool table and the bar; only there from inside.
    const planks = mesh(
      new THREE.PlaneGeometry(L + 0.3, D + 0.3),
      texMat(woodTexture(), { color: '#b08560', rough: 0.9 }),
      0,
      H,
      0,
      { cast: false },
    );
    planks.rotation.x = Math.PI / 2;
    this.ceiling.add(planks);
    const lampGlow = mat('#ffd9a0', { emissive: '#ffbf70', emissiveIntensity: 1.4 });
    for (const [x, z] of [
      [BAR_SPOTS.pool.x, BAR_SPOTS.pool.z],
      [-2.5, -3.6],
      [BAR_SPOTS.armTable.x, BAR_SPOTS.armTable.z],
      [-6.5, 0.2],
    ] as const) {
      this.ceiling.add(cyl(0.01, 0.01, 0.9, M.black, x, H - 0.45, z, 4));
      this.ceiling.add(
        cyl(0.08, 0.32, 0.22, mat('#2f5a3a', { rough: 0.5 }), x, H - 1.0, z, 12, { cast: false }),
      );
      this.ceiling.add(sphere(0.1, lampGlow, x, H - 1.08, z, 8));
    }
    this.ceiling.visible = false;
    this.group.add(this.ceiling);

    // Swinging doors on the inside of the front wall.
    const door = new THREE.Group();
    door.add(box(1.8, 2.3, 0.05, mat('#24180f'), 0, 1.15, 0));
    for (const s of [-1, 1])
      door.add(box(0.85, 1.1, 0.06, mat('#9a6a3e'), s * 0.45, 1.5, -0.05, { cast: false }));
    door.position.set(doorX, 0, D / 2);
    front?.upper.add(door);

    const place = <T extends THREE.Object3D>(
      obj: T,
      p: Vec2,
      rot = 0,
      parent: THREE.Object3D = this.group,
    ): T => {
      obj.position.x = p.x;
      obj.position.z = p.z;
      obj.rotation.y = rot;
      parent.add(obj);
      return obj;
    };
    const solid = (p: Vec2, w: number, d: number): void => void collision.addRect(c.x + p.x, c.z + p.z, w, d);

    // The bar, the back bar and stools.
    place(barCounter(7), { x: -2.5, z: -4.0 });
    solid({ x: -2.5, z: -4.0 }, 7.1, 0.86);
    const bb = backBar(7, this.rng);
    place(bb, { x: -2.5, z: -5.65 });
    solid({ x: -2.5, z: -5.65 }, 7, 0.5);
    for (let i = 0; i < 6; i++) place(barStool(), { x: -5.5 + i * 1.2, z: -3.25 });
    // A sign over the bar.
    const sign = signTexture({
      w: 768,
      h: 192,
      bg: '#1b1410',
      fg: '#ff9a3c',
      border: '#ff9a3c',
      borderWidth: 8,
      lines: [
        { text: 'THE RUSTY SPUR', size: 90, font: 'Rye' },
        { text: 'NO SHIRT • NO SHOES • NO PROBLEM', size: 38, font: 'Anton', color: '#5ff3ff' },
      ],
    });
    const signMat = new THREE.MeshStandardMaterial({
      map: sign,
      emissive: new THREE.Color('#ffffff'),
      emissiveMap: sign,
      emissiveIntensity: 0.9,
    });
    back?.upper.add(
      place(
        mesh(new THREE.PlaneGeometry(3.6, 0.9), signMat, 0, 2.7, 0, { cast: false }),
        { x: -2.5, z: -D / 2 + 0.01 },
        0,
        new THREE.Group(),
      ),
    );
    const neon1 = neonBeerSign();
    neon1.position.set(4, 2.0, -D / 2 + 0.02);
    back?.upper.add(neon1);
    const neon2 = neonBeerSign();
    neon2.position.set(-L / 2 + 0.02, 2.0, -3);
    neon2.rotation.y = Math.PI / 2;
    west?.upper.add(neon2);
    // Pool table.
    this.props.pool = place(poolTable(), BAR_SPOTS.pool);
    solid(BAR_SPOTS.pool, 2.5, 1.4);
    // Darts on the west wall.
    const board = dartboard();
    board.group.position.set(-L / 2 + 0.05, 1.7, BAR_SPOTS.dartboard.z);
    board.group.rotation.y = Math.PI / 2;
    west?.upper.add(board.group);
    this.dartboard = board.group;
    this.chalk = board.chalk;
    this.group.add(
      box(0.08, 0.02, 1.2, mat('#ffd400'), BAR_SPOTS.darts.x + 0.4, 0.012, BAR_SPOTS.darts.z, {
        cast: false,
      }),
    );
    // Mechanical bull.
    const pen = mechBullPen();
    place(pen.group, BAR_SPOTS.bull);
    this.mechBull = pen.bull;
    collision.addCircle(c.x + BAR_SPOTS.bull.x, c.z + BAR_SPOTS.bull.z, 2.3);
    // Video poker along the east wall.
    const poker = pokerMachine();
    this.props.poker = place(poker.group, BAR_SPOTS.poker, -Math.PI / 2);
    this.pokerScreen = poker.screen;
    this.pokerScreenMesh = poker.screenMesh;
    solid(BAR_SPOTS.poker, 0.6, 0.7);
    const poker2 = pokerMachine();
    place(poker2.group, { x: BAR_SPOTS.poker.x, z: BAR_SPOTS.poker.z + 1.0 }, -Math.PI / 2);
    solid({ x: BAR_SPOTS.poker.x, z: BAR_SPOTS.poker.z + 1.0 }, 0.6, 0.7);
    void east;
    // Arm-wrestling table with an elbow pad, and a couple of high-tops.
    const arm = barTable();
    arm.add(box(0.5, 0.02, 0.18, mat('#c62828'), 0, 0.785, 0, { cast: false }));
    this.props.arm = place(arm, BAR_SPOTS.armTable);
    collision.addCircle(c.x + BAR_SPOTS.armTable.x, c.z + BAR_SPOTS.armTable.z, 0.5);
    for (const dz of [-0.75, 0.75])
      place(barStool(), { x: BAR_SPOTS.armTable.x, z: BAR_SPOTS.armTable.z + dz });
    for (const p of [
      { x: 2.2, z: -1.4 },
      { x: -0.4, z: -1.0 },
    ]) {
      place(barTable(), p);
      collision.addCircle(c.x + p.x, c.z + p.z, 0.5);
      for (const a of [0.6, 2.5, 4.3])
        place(barStool(), { x: p.x + Math.cos(a) * 0.75, z: p.z + Math.sin(a) * 0.75 });
    }
    // Jukebox by the door.
    const juke = jukebox();
    this.props.jukebox = place(juke.group, BAR_SPOTS.jukebox, Math.PI / 2);
    this.jukeGlow = juke.glow;
    solid(BAR_SPOTS.jukebox, 0.6, 0.9);
    registerNightGlow(this.jukeGlow, 1.2);
    parent.add(this.group);
  }

  get bounds(): ReturnType<typeof roomBounds> {
    return roomBounds(BAR_SLOT, 'bar');
  }

  world(p: Vec2): Vec2 {
    return { x: this.center.x + p.x, z: this.center.z + p.z };
  }

  get entry(): Vec2 {
    const s = ROOM_SHAPE.bar;
    return this.world({ x: s.doorX, z: s.depth / 2 - 0.9 });
  }

  get exit(): Vec2 {
    const s = ROOM_SHAPE.bar;
    return this.world({ x: s.doorX, z: s.depth / 2 - 0.4 });
  }

  updateCutaway(camera: THREE.Vector3): void {
    for (const w of this.walls) {
      const out = (camera.x - w.px) * w.nx + (camera.z - w.pz) * w.nz;
      w.upper.visible = out < 0.2;
    }
    const b = this.bounds;
    this.ceiling.visible =
      camera.y < ROOM_SHAPE.bar.height &&
      camera.x > b.minX &&
      camera.x < b.maxX &&
      camera.z > b.minZ &&
      camera.z < b.maxZ;
  }

  update(time: number, dt: number): void {
    this.bucking = Math.max(0, this.bucking - dt);
    if (!this.bullDriven) {
      const b = this.bucking > 0 ? 1 : 0.12;
      this.mechBull.rotation.y = Math.sin(time * 2.2) * 0.6 * b;
      this.mechBull.rotation.z = Math.sin(time * 7.3) * 0.18 * b;
      this.mechBull.rotation.x = Math.sin(time * 5.1 + 1) * 0.12 * b;
    }
    this.jukeGlow.emissiveIntensity = 0.8 + Math.sin(time * 3) * 0.25;
    this.pokerScreen.emissiveIntensity = 0.7 + Math.sin(time * 9) * 0.1;
  }
}
